import { Card } from "../../../components/Card";
import { SelectInput } from "../../../components/SelectInput/SelectInput";
import SearchView from "../../engagement-report/components/SearchView";
import { StatusCounter } from "./components/StatusCounter";
import { useUser } from "../../../services/user-context";
import { useApp } from "../../../services/app-context";
import { useState, useEffect, useRef } from "react";
import { CertificationCardListSkeleton } from "./components/CertificationCardSkeleton";
import { twMerge } from "tailwind-merge";
import { getLabel } from "../utils/labels";

const getCertificationOptions = (labels: any) => [
    {
        label: `Active ${getLabel("certificationName.plural", labels, true)}`,
        value: "active"
    },
    {
        label: `Inactive ${getLabel("certificationName.plural", labels, true)}`,
        value: "inactive"
    }
]

function CertificationCard({ certification, onClick, onDelete, isSelected }: { certification: any, onClick: () => void, onDelete: () => void, isSelected: boolean }) {
    const { certificationAssets } = useApp();
    const statusData = certificationAssets?.userStatus;
    return (
        <div role="button" onClick={onClick} className="flex flex-col gap-2">
            <div className={twMerge("flex flex-1 gap-6 hover:bg-[#F5F5F5] rounded-[6px] px-2 py-2.5 justify-between flex-wrap flex-col", isSelected && "bg-[#F5F5F5]")}>
                <div className="flex flex-col gap-1.5">
                    <span title={certification.certificationName} className="text-text2 text-sm font-medium leading-[15px] w-full overflow-hidden text-ellipsis whitespace-nowrap">{certification.certificationName}</span>
                    <span className="text-[#242424] text-xs font-light leading-[16px]">{certification.totalAssignments} Assigned Users</span>

                    <StatusCounter statusData={statusData} certification={certification} />
                    <div className="flex items-center gap-2 flex-wrap">
                        {
                            certification?.counts?.map((count: any) => {
                                return (
                                    <div className="text-[#242424] flex items-center gap-1 text-[clamp(8px,1.2vw,10px)]" key={count.status}>
                                        <div className="w-[clamp(5px,1.2vw,8px)] h-[clamp(5px,1.2vw,8px)] rounded-full" style={{ backgroundColor: statusData?.[count.status]?.statusBackgroundColor }} />
                                        <span>{count?.count}</span>
                                        <span>{statusData?.[count.status]?.label}</span>
                                    </div>
                                )
                            })
                        }
                    </div>
                </div>
            </div>
            <div className="border-b-[0.5px] border-b-[#DADADA]" />
        </div>
    )
}

export function CertificationList({ filter, setFilter, certifications, certificationsMeta, isLoading, onCreateCertification, onSelectCertification, onDeleteCertification, selectedCertification, fetchNextPage, hasNextPage, isFetchingNextPage }: { filter: any, setFilter: (filter: any) => void, certifications: any[], certificationsMeta?: any, isLoading: boolean, onCreateCertification: () => void, onSelectCertification: (certification: any) => void, onDeleteCertification: (certification: any) => void, selectedCertification: any, fetchNextPage: () => void, hasNextPage?: boolean, isFetchingNextPage: boolean }) {
    const { certificationAssets } = useApp();
    const labels = certificationAssets?.labels || {};
    const { userInfo } = useUser() as any;
    const role = userInfo?.role;
    const sentinelRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!sentinelRef.current || !scrollContainerRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                }
            },
            {
                root: scrollContainerRef.current,
                rootMargin: '200px',
                threshold: 0.1,
            }
        );

        observer.observe(sentinelRef.current);

        return () => observer.disconnect();
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    const [localIsActive, setLocalIsActive] = useState(filter?.isActive ?? "active");
    useEffect(() => {
        setLocalIsActive(filter?.isActive ?? "active");
    }, [filter?.isActive]);

    const handleIsActiveChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;
        setLocalIsActive(value);
        setFilter((prev) => ({ ...prev, isActive: value }));
    };

    const [localName, setLocalName] = useState(filter?.name ?? "");
    useEffect(() => {
        setLocalName(filter?.name ?? "");
    }, [filter?.name]);

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setLocalName(value);
        setFilter((prev) => ({ ...prev, name: value }));
    };

    return (
        <div className="flex flex-col gap-2 !h-[calc(100vh-175px)]">
            <div className="flex items-center gap-2">
                <SelectInput
                    options={getCertificationOptions(labels)}
                    value={localIsActive || ""}
                    onChange={handleIsActiveChange}
                    placeholder={`Select ${getLabel("certificationName.plural", labels)}`}
                    className="!py-2.5 !px-3 text-sm font-medium flex-[3] !border-none text-primary uppercase"
                    containerClassName="flex-[3]"
                />
            </div>
            <div>
                <SearchView value={localName || ""} onChange={handleNameChange} className="!py-2.5" />
            </div>
            {isLoading ? <CertificationCardListSkeleton /> : <Card className="flex-1 !px-0 ">
                <div className="flex items-center justify-between px-6">
                    <div className="flex flex-col gap-1.5">
                        <span className="uppercase  text-primary text-base font-semibold leading-[16px]">{getLabel("certificationName.plural", labels)}</span>
                    </div>
                    <span className="text-text2 text-xs font-light leading-[16px]">{certificationsMeta?.total ?? certifications?.length} {localIsActive === "active" ? "Active" : "Inactive"} {getLabel("session.plural", labels)}</span>
                </div>
                {!isLoading && certifications?.length === 0 && <div className="flex items-center justify-center h-full">
                    <div className="text-text2 text-sm font-medium leading-[15px]">
                        No {getLabel("session.plural", labels)} found.
                    </div>
                </div>}
                {!isLoading && certifications?.length > 0 && <div ref={scrollContainerRef} className="flex flex-col gap-2 mt-3 px-6 h-[calc(100vh-350px)] overflow-auto">
                    {certifications?.map((item) => (
                        <CertificationCard key={item.id} certification={item} onClick={() => onSelectCertification(item)} onDelete={() => onDeleteCertification(item)} isSelected={selectedCertification?.id === item.id} />
                    ))}
                    {hasNextPage && (
                        <div ref={sentinelRef} className="h-px w-full" />
                    )}
                    {isFetchingNextPage && (
                        <div className="text-center py-2">
                            <span className="text-text-gray text-sm">Loading more...</span>
                        </div>
                    )}
                </div>}
            </Card>}
        </div>
    );
}